import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Error from "../utils/Error";
import BlogSkeleton from "./BlogSkeleton";
import { formatDisplayDate } from "../utils/fomatDate";
import { useBlogs, useSearchBlogs } from "../../hooks/useBlog";
import he from "he";
import { useQueryClient } from "@tanstack/react-query";
import { IconArrowRight, IconCalendar, IconHeart } from "../brand/icons";
import { ALL_TOPICS, tagsOf } from "./blogTopics";

const EXCERPT_LIMIT = 150;
const WORDS_PER_MINUTE = 200;

/** The CMS double-escapes its HTML (`&amp;nbsp;`, `&lt;p&gt;`), so decode twice. */
const toPlainText = (html) =>
  he.decode(he.decode(String(html ?? "")))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const BlogCard = ({ item }) => {
  const plain = toPlainText(item.blog_content);
  const excerpt =
    plain.length > EXCERPT_LIMIT
      ? `${plain.slice(0, EXCERPT_LIMIT).trimEnd()}…`
      : plain;
  const readMinutes = Math.max(
    1,
    Math.round(plain.split(/\s+/).filter(Boolean).length / WORDS_PER_MINUTE),
  );
  const topic = tagsOf(item)[0];

  return (
    <article className="group h-full overflow-hidden rounded-2xl border border-hairline bg-surface-white shadow-comfort transition-shadow duration-200 hover:shadow-brand-md">
      <Link
        to={`/blog/${item.id}`}
        className="flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-surface-light">
          {item.blog_image_url && (
            <img
              src={item.blog_image_url}
              alt={item.blog_title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}
          {topic && (
            <span className="absolute left-4 top-4 max-w-[calc(100%-2rem)] truncate rounded-full bg-white-chip px-3 py-1 text-label-sm text-spectrum-blue shadow-brand-sm backdrop-blur-sm">
              {topic}
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <div className="mb-3 flex items-center gap-2 text-label-sm text-muted">
            <IconCalendar size={16} className="shrink-0" />
            <span>
              {formatDisplayDate(item?.created_at)} · {readMinutes} min read
            </span>
          </div>

          <h2 className="mb-3 font-headline text-headline-sm text-ink transition-colors duration-150 group-hover:text-spectrum-blue">
            {item.blog_title}
          </h2>

          <p className="mb-6 flex-1 text-body-md leading-relaxed text-muted">
            {excerpt}
          </p>

          <div className="mt-auto flex items-center justify-between gap-3 border-t border-cloud-mist pt-3">
            <span className="inline-flex items-center gap-1 text-label-md text-spectrum-blue">
              Read Article
              <IconArrowRight
                size={18}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </span>
            <IconHeart filled size={18} className="shrink-0 text-heart-teal" />
          </div>
        </div>
      </Link>
    </article>
  );
};

const BlogList = ({ children }) => (
  <div className="w-full bg-surface-white pb-20 pt-4">
    <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 md:px-10">{children}</div>
  </div>
);

const Blog2 = ({ category = ALL_TOPICS }) => {
  const { data: blog, isLoading, error, refetch } = useBlogs();
  const { data: searchBlogs, isFetching } = useSearchBlogs();
  const blogs = searchBlogs?.data?.length > 0 ? searchBlogs : blog;
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ["one"] });
    refetch();
  }, []);

  const posts = useMemo(() => {
    const list = Array.isArray(blogs?.data) ? blogs.data : [];
    const visible =
      category === ALL_TOPICS
        ? list
        : list.filter((item) =>
            tagsOf(item).some((tag) => tag.toLowerCase() === category.toLowerCase()),
          );
    return visible
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [blogs, category]);

  if (isLoading || isFetching)
    return (
      <BlogList>
        <BlogSkeleton />
      </BlogList>
    );
  if (error)
    return (
      <BlogList>
        <Error
          title="The latest stories aren’t loading"
          message={error.message}
          onRetry={refetch}
        />
      </BlogList>
    );

  return (
    <BlogList>
      {posts.length === 0 ? (
        <p className="rounded-2xl border border-hairline bg-surface-light p-8 text-center text-body-md text-muted">
          No blogs found
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {posts.map((item) => (
            <BlogCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </BlogList>
  );
};

export default Blog2;
